import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import { formatCommunicationPhone } from './communications-model';
const notify = () => window.dispatchEvent(new Event('workview:texts-changed'));
export function CallSpamButton({ callId, initiallyBlocked = false, onBlocked, compact = false }: { callId: string; initiallyBlocked?: boolean | number; onBlocked?: () => void; compact?: boolean }) {
  const [blocked, setBlocked] = useState(Boolean(initiallyBlocked));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function save() {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const response = await apiFetch(`/api/v1/workspace/communications/live-calls/${encodeURIComponent(callId)}/spam`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ blocked: !blocked }) });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(response.status === 404 ? 'Call blocking is waiting for the API update.' : payload?.error?.message || 'This number could not be updated. Please try again.');
      }
      setBlocked(value => !value); notify();
      if (!blocked) onBlocked?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'This number could not be updated.'); }
    finally { setBusy(false); }
  }
  return <div className={compact ? "comms-call-spam-chip" : undefined}><button type="button" className={compact ? "comms-call-chip" : "comms-link"} title={compact ? blocked ? "Allow future calls and texts" : "Block future calls and texts" : undefined} disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : blocked ? 'Unblock number' : 'Mark as spam'}</button>{!compact && <small className="comms-muted block">{blocked ? 'Future calls and texts blocked.' : 'Blocks future calls and texts.'}</small>}{error && <p role="alert" className="comms-review-error">{error}</p>}</div>;
}
type Block = { phone: string; blockedAt: string };
export function BlockedPhoneNumbers() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Block[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const refresh = () => { setPage(0); setRevision(value => value + 1); };
    window.addEventListener('workview:texts-changed', refresh);
    return () => window.removeEventListener('workview:texts-changed', refresh);
  }, []);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController(); setLoading(true); setError('');
    void apiFetch(`/api/v1/workspace/communications/blocked-numbers?page=${page}`, { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error(response.status === 404 ? 'Blocked numbers are waiting for the API update.' : 'Blocked numbers could not be loaded. Please try again.');
        const payload = await response.json() as { data: { items: Block[]; hasMore: boolean } };
        if (controller.signal.aborted) return;
        setItems(current => page === 0 ? payload.data.items : [...current, ...payload.data.items.filter(item => !current.some(existing => existing.phone === item.phone))]);
        setHasMore(payload.data.hasMore);
      }).catch(cause => { if (!controller.signal.aborted) setError(cause.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [open, page, revision]);
  async function unblock(phone: string) {
    if (busy || loading) return;
    setBusy(phone); setError('');
    try {
      const response = await apiFetch(`/api/v1/workspace/communications/blocked-numbers/${encodeURIComponent(phone)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('This number could not be unblocked. Please try again.');
      setItems(current => current.filter(item => item.phone !== phone)); notify();
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'This number could not be unblocked.'); }
    finally { setBusy(''); }
  }
  return <section className="mt-4"><button type="button" className="comms-link" aria-expanded={open} onClick={() => { setOpen(value => !value); setPage(0); }}>Blocked numbers</button>
    {open && <><p className="comms-muted">These numbers cannot ring the office or add new texts to WorkView.</p>
      {items.length > 0 && <ul className="comms-blocked-list">{items.map(item => <li key={item.phone}><span>{formatCommunicationPhone(item.phone)}</span><button type="button" className="comms-link" disabled={Boolean(busy) || loading} aria-label={`Unblock ${formatCommunicationPhone(item.phone)}`} onClick={() => void unblock(item.phone)}>{busy === item.phone ? 'Unblocking…' : 'Unblock'}</button></li>)}</ul>}
      {loading && <p className="comms-muted" role="status">Loading blocked numbers…</p>}
      {!loading && !error && !items.length && <p className="comms-muted">No blocked numbers.</p>}
      {error && <p role="alert" className="comms-review-error">{error} <button type="button" className="comms-link" disabled={loading} onClick={() => setRevision(value => value + 1)}>Try again</button></p>}
      {hasMore && <button type="button" className="comms-link" disabled={loading || Boolean(busy)} onClick={() => setPage(value => value + 1)}>Load more numbers</button>}
    </>}
  </section>;
}
